import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { api, money, shopToday } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatPhone } from "@/lib/phone";
import { deliverFile } from "@/lib/pdfFiling";

const PERIODS = [
  { key: "week", label: "This week" },
  { key: "month", label: "Monthly" },
  { key: "quarter", label: "Quarterly" },
  { key: "half", label: "6 monthly" },
  { key: "year", label: "Yearly" },
  { key: "custom", label: "Custom range" },
];

const OUTCOMES = [
  ["in_stock", "Still in stock"],
  ["melted", "Melted"],
  ["refinery", "Sent to refinery"],
  ["sold", "Sold on"],
  ["scrapped", "Scrapped"],
];

export default function OldGoldReport() {
  const { branch, can } = useAuth();
  const [selected, setSelected] = useState([]);
  const [outcome, setOutcome] = useState("melted");
  const [outcomeDate, setOutcomeDate] = useState(shopToday());
  const [outcomeNotes, setOutcomeNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [period, setPeriod] = useState("month");
  const [fromDate, setFromDate] = useState(shopToday());
  const [toDate, setToDate] = useState(shopToday());
  const [allBranches, setAllBranches] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = useCallback(
    () => ({
      period,
      ...(period === "custom" ? { from_date: fromDate, to_date: toDate } : {}),
      ...(allBranches ? {} : { branch }),
    }),
    [period, fromDate, toDate, allBranches, branch]
  );

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/reports/old-gold", { params: params() })
      .then(({ data }) => setData(data))
      .catch((err) =>
        toast.error(
          (err.response?.data?.detail && String(err.response.data.detail)) ||
            "Could not build that report"
        )
      )
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadCsv = async () => {
    try {
      const res = await api.get("/reports/old-gold.csv", {
        params: params(),
        responseType: "blob",
      });
      await deliverFile(
        res.data,
        "Reports",
        `old-gold-${data?.from_date}-to-${data?.to_date}.csv`,
        toast
      );
    } catch (e) {
      toast.error("Could not download the CSV");
    }
  };

  const t = data?.totals;
  const mayRecord = can("documents:void");
  const contractIds = [
    ...new Set((data?.rows || []).filter((r) => r.doc_id && r.source === "Buying contract")
      .map((r) => r.doc_id)),
  ];
  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const recordOutcome = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.post("/documents/disposal", {
        doc_ids: selected,
        outcome,
        outcome_date: outcomeDate,
        notes: outcomeNotes,
      });
      toast.success(
        `${res.updated} purchase${res.updated === 1 ? "" : "s"} marked ${
          OUTCOMES.find(([k]) => k === outcome)?.[1]
        }`
      );
      setSelected([]);
      setOutcomeNotes("");
      load();
    } catch (err) {
      toast.error(
        (err.response?.data?.detail && String(err.response.data.detail)) ||
          "Could not save that"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fade-up" data-testid="old-gold-report">
      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#B38F4E]">
        {allBranches ? "All branches" : branch}
      </p>
      <h1 className="mt-2 text-3xl font-bold sm:text-4xl">Old Gold Purchased</h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Every gram of old gold bought — buying contracts and part-exchange trade-ins on
        invoices — for ATO records and the Second-Hand Dealer register.
      </p>

      <div className="mt-6 flex flex-wrap items-end gap-2" data-testid="old-gold-filters">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setPeriod(p.key)}
            className={`border px-4 py-2 text-sm transition-colors duration-150 ${
              period === p.key
                ? "border-[#C5A059] bg-[#FBF8F2] font-semibold"
                : "border-border hover:bg-muted"
            }`}
            data-testid={`period-${p.key}`}
          >
            {p.label}
          </button>
        ))}
        {period === "custom" && (
          <>
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                From
              </Label>
              <Input
                type="date"
                className="mt-1 h-10 w-[160px] rounded-none"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                data-testid="old-gold-from-date"
              />
            </div>
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                To
              </Label>
              <Input
                type="date"
                className="mt-1 h-10 w-[160px] rounded-none"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                data-testid="old-gold-to-date"
              />
            </div>
          </>
        )}
        <label className="flex items-center gap-2 px-2 text-sm">
          <input
            type="checkbox"
            checked={allBranches}
            onChange={(e) => setAllBranches(e.target.checked)}
            data-testid="old-gold-all-branches"
          />
          Both stores
        </label>
        <Button
          variant="outline"
          className="rounded-none"
          onClick={downloadCsv}
          data-testid="old-gold-csv-button"
        >
          <Download className="mr-1.5 h-4 w-4" /> CSV
        </Button>
      </div>

      {data && (
        <p className="mt-4 text-xs uppercase tracking-wider text-muted-foreground" data-testid="old-gold-range">
          {data.from_date} to {data.to_date}
        </p>
      )}

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          ["Total weight bought", `${(t?.weight ?? 0).toFixed(3)} gm`, "old-gold-total-weight"],
          ["Total amount paid", money(t?.amount ?? 0), "old-gold-total-amount"],
          ["Melted", `${(t?.weight_melted ?? 0).toFixed(3)} gm`, "old-gold-weight-melted"],
          ["Still in stock", `${(t?.weight_in_stock ?? 0).toFixed(3)} gm`, "old-gold-weight-in-stock"],
          ["Transactions", String(t?.transactions ?? 0), "old-gold-transactions"],
          ["Lines without ID photo", String(t?.missing_id ?? 0), "old-gold-missing-id"],
        ].map(([label, value, testid]) => (
          <div key={testid} className="border border-border p-5">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {label}
            </div>
            <div className="tabular mt-2 text-2xl font-bold" data-testid={testid}>
              {value}
            </div>
          </div>
        ))}
      </div>

      {mayRecord && (
        <div className="mt-6 border border-border bg-[#FBF8F2] p-4" data-testid="disposal-panel">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                What was done with the gold
              </Label>
              <select
                className="mt-1 h-10 w-[190px] border border-input bg-white px-2 text-sm"
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
                data-testid="disposal-outcome"
              >
                {OUTCOMES.map(([k, label]) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                On date
              </Label>
              <Input
                type="date"
                className="mt-1 h-10 w-[160px] rounded-none"
                value={outcomeDate}
                onChange={(e) => setOutcomeDate(e.target.value)}
                data-testid="disposal-date"
              />
            </div>
            <div className="min-w-[220px] flex-1">
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Notes (optional)
              </Label>
              <Input
                className="mt-1 h-10 rounded-none"
                placeholder="Melt batch 12, refinery docket 4471…"
                value={outcomeNotes}
                onChange={(e) => setOutcomeNotes(e.target.value)}
                data-testid="disposal-notes"
              />
            </div>
            <Button
              onClick={recordOutcome}
              disabled={saving || selected.length === 0}
              className="h-10 rounded-none bg-[#C5A059] px-5 font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
              data-testid="disposal-save-button"
            >
              Record for {selected.length} selected
            </Button>
            <button
              type="button"
              onClick={() => setSelected(selected.length ? [] : contractIds)}
              className="pb-2 text-xs uppercase tracking-wider text-muted-foreground underline hover:text-[#0A0A0A]"
              data-testid="disposal-select-all"
            >
              {selected.length ? "Clear selection" : "Select all purchases"}
            </button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Tick the purchases below, then record it — melt a whole batch in one go. Only
            buying contracts can be marked; trade-ins taken on an invoice stay with that
            invoice.
          </p>
        </div>
      )}

      <div className="mt-6 overflow-x-auto border border-border">
        <table className="w-full min-w-[1040px] text-sm" data-testid="old-gold-table">
          <thead className="bg-[#0A0A0A] text-white">
            <tr>
              {mayRecord && <th className="w-10 px-2 py-2.5" />}
              {["Date", "Number", "Source", "Customer", "Phone", "ID number", "Description",
                "Weight (gm)", "Amount paid", "ID", "What happened"].map((h) => (
                <th
                  key={h}
                  className={`px-3 py-2.5 font-semibold ${
                    ["Weight (gm)", "Amount paid"].includes(h) ? "text-right" : "text-left"
                  }`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(!data?.rows || data.rows.length === 0) && (
              <tr>
                <td colSpan={mayRecord ? 12 : 11} className="px-3 py-10 text-center text-muted-foreground">
                  {loading ? "Building report…" : "No old gold bought in this period."}
                </td>
              </tr>
            )}
            {(data?.rows || []).map((r, i) => (
              <tr key={i} className="hover:bg-[#FBF8F2]" data-testid={`old-gold-row-${i}`}>
                {mayRecord && (
                  <td className="px-2">
                    {r.source === "Buying contract" && (
                      <input
                        type="checkbox"
                        checked={selected.includes(r.doc_id)}
                        onChange={() => toggle(r.doc_id)}
                        data-testid={`old-gold-select-${r.doc_number}`}
                      />
                    )}
                  </td>
                )}
                <td className="tabular px-3 py-2.5">{r.date}</td>
                <td className="tabular px-3 py-2.5 font-semibold">#{r.doc_number}</td>
                <td className="px-3 py-2.5 text-xs uppercase tracking-wider">{r.source}</td>
                <td className="px-3 py-2.5">{r.customer_name}</td>
                <td className="tabular px-3 py-2.5">{r.customer_phone ? formatPhone(r.customer_phone) : "-"}</td>
                <td className="px-3 py-2.5">{r.id_number || "-"}</td>
                <td className="px-3 py-2.5">{r.description}</td>
                <td className="tabular px-3 py-2.5 text-right">{r.weight.toFixed(3)}</td>
                <td className="tabular px-3 py-2.5 text-right font-semibold">{money(r.amount)}</td>
                <td className="px-3 py-2.5">
                  {r.id_photos > 0 ? (
                    <span className="text-[#166534]">{r.id_photos}</span>
                  ) : (
                    <span className="text-[#9A3412]">none</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-xs" data-testid={`old-gold-outcome-${r.doc_number}`}>
                  {r.source === "Buying contract" ? (
                    <>
                      <span
                        className={
                          r.outcome && r.outcome !== "in_stock"
                            ? "font-semibold text-[#166534]"
                            : "text-muted-foreground"
                        }
                      >
                        {r.outcome_label}
                      </span>
                      {r.outcome_date && (
                        <span className="tabular block text-[11px] text-muted-foreground">
                          {r.outcome_date}
                        </span>
                      )}
                      {r.outcome_notes && (
                        <span className="block text-[11px] text-muted-foreground">
                          {r.outcome_notes}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
